import { RegisterStepHeader } from "@/components/molecules/RegisterStepHeader";

type HeaderRegisterStep = "email" | "personalInfo" | "phone";
type HeaderEmailSubstep = "input" | "otp";
type HeaderPersonalInfoSubstep = "input" | "password";
type HeaderPhoneSubstep = "input" | "otp" | "consent";

interface RenderStepHeaderProps {
  step: HeaderRegisterStep;
  emailSubstep: HeaderEmailSubstep;
  personalInfoSubstep: HeaderPersonalInfoSubstep;
  phoneSubstep: HeaderPhoneSubstep;
  email: string;
  phone?: string;
}

export function maskPhoneForOtpHeader(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const last4 = digits.slice(-4).padStart(4, "*");
  return `***-***-${last4}`;
}

export const stepHeaders = {
  email: {
    input: {
      title: "Start Smart Homeownership",
      subtitle: "Let’s begin with your email address. We'll send you a verification link to make sure we’ve got it right.",
    },
    otp: {
      title: "Verify Your Email",
      subtitle: "Enter the 6-digit code we sent to your email.",
    },
  },
  personalInfo: {
    input: {
      title: "Let’s Get Some Info...",
      subtitle: "Enter your details so we can locate your mortgage and set up your account.",
    },
    password: {
      title: "Set Your Password",
      subtitle: "Come up with a good one, so no one can guess it!",
    },
  },
  phone: {
    input: {
      title: "What’s Your Number?",
      subtitle: "We’ll use this to verify your account—keeping your info safe is #1 on our list.",
    },
    otp: {
      title: "Verify Your Phone",
      subtitle: "Enter the 6-digit code we sent to your phone.",
    },
    consent: {
      title: "Do You Want to Set up Multi-Factor Authentication (MFA) for This Mobile Number: [***-***-1234]?",
      subtitle: "Most Freedom customers find it a quick-and-easy way to keep their account secure.",
    },
  },
};

interface StepHeaderConfig {
  title: string;
  subtitle: string;
  subtitleClassName?: string;
}

export function RenderStepHeader({
  step,
  emailSubstep,
  personalInfoSubstep,
  phoneSubstep,
  email,
  phone,
}: RenderStepHeaderProps) {
  // Helper for masking email
  const maskEmail = (emailValue: string) => {
    const [user, domain] = emailValue.split("@");
    if (!user || !domain) return emailValue;
    return user.length <= 2
      ? `${user[0]}****@${domain}`
      : `${user[0]}****${user.slice(-1)}@${domain}`;
  };

  const maskedPhoneForConsent = (phoneValue?: string) => {
    if (!phoneValue?.trim()) {
      return "[***-***-****]";
    }
    return `[${maskPhoneForOtpHeader(phoneValue)}]`;
  };

  const configMap: Record<
    HeaderRegisterStep,
    Record<string, StepHeaderConfig>
  > = {
    email: {
      input: {
        title: stepHeaders.email.input.title,
        subtitle: stepHeaders.email.input.subtitle,
      },
      otp: {
        title: stepHeaders.email.otp.title,
        subtitle: `Enter the 6-digit code we sent to ${maskEmail(email)}.`,
      },
    },
    personalInfo: {
      input: {
        title: stepHeaders.personalInfo.input.title,
        subtitle: stepHeaders.personalInfo.input.subtitle,
      },
      password: {
        title: stepHeaders.personalInfo.password.title,
        subtitle: stepHeaders.personalInfo.password.subtitle,
      },
    },
    phone: {
      input: {
        title: stepHeaders.phone.input.title,
        subtitle: stepHeaders.phone.input.subtitle,
      },
      otp: {
        title: stepHeaders.phone.otp.title,
        subtitle: stepHeaders.phone.otp.subtitle,
      },
      consent: {
        title: `Do You Want to Set up Multi-Factor Authentication (MFA) for This Mobile Number: ${maskedPhoneForConsent(phone)}?`,
        subtitle: stepHeaders.phone.consent.subtitle,
        subtitleClassName: "md:text-sm md:leading-5",
      },
    },
  };

  let headerConfig: StepHeaderConfig | undefined;
  if (step === "email") {
    headerConfig = configMap.email[emailSubstep];
  } else if (step === "personalInfo") {
    headerConfig = configMap.personalInfo[personalInfoSubstep];
  } else if (step === "phone") {
    headerConfig = configMap.phone[phoneSubstep];
  }

  return headerConfig ? (
    <RegisterStepHeader
      title={headerConfig.title}
      subtitle={headerConfig.subtitle}
      subtitleClassName={headerConfig.subtitleClassName}
    />
  ) : null;
}
